import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ContentAnalyticsItem {
  id: string;
  title: string;
  category_name: string | null;
  content_type_name: string | null;
  total_plays: number;
  total_likes: number;
  total_downloads: number;
  avg_completion_rate: number;
  total_returns: number;
}

interface AnalyticsChartsProps {
  data: ContentAnalyticsItem[];
}

export const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({ data }) => {
  // Overall completion rate
  const overallCompletion = useMemo(() => {
    if (data.length === 0) return { completed: 0, notCompleted: 100 };
    
    const totalCompletion = data.reduce((sum, item) => sum + item.avg_completion_rate, 0);
    const avgCompletion = totalCompletion / data.length;
    
    return {
      completed: Math.round(avgCompletion),
      notCompleted: Math.round(100 - avgCompletion),
    };
  }, [data]);

  const completionData = [
    { name: 'Completed', value: overallCompletion.completed },
    { name: 'Not Completed', value: overallCompletion.notCompleted },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Completion Rate */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: { xs: '100%', md: '400px' } }}>
          <Paper sx={{ padding: 2 }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Overall Completion Rate
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={completionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percent, name }) => {
                    const color = name === 'Completed' ? '#69B97E' : '#d32f2f';
                    return <tspan fill={color}>{((percent || 0) * 100).toFixed(0)}%</tspan>;
                  }}
                  outerRadius={100}
                  innerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                  isAnimationActive={false}
                >
                  {completionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={index === 0 ? '#69B97E' : '#E3E8EE'} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any, name: string) => {
                  const color = name === 'Completed' ? '#69B97E' : '#d32f2f';
                  return <span style={{ color }}>{value}%</span>;
                }} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value, entry: any) => {
                    const data = completionData.find(d => d.name === value);
                    const color = value === 'Completed' ? '#69B97E' : '#d32f2f';
                    return (
                      <span style={{ color, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span 
                          style={{ 
                            display: 'inline-block', 
                            width: '12px', 
                            height: '12px', 
                            backgroundColor: color,
                            flexShrink: 0
                          }} 
                        />
                        {value}: {data?.value || 0}%
                      </span>
                    );
                  }}
                  iconSize={0}
                  wrapperStyle={{ paddingLeft: 0 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

